/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import dynamicMenuService from 'features/MenuBook/dynamicMenuService';
import { SubGenre } from 'features/MenuBook/models';
import {
  DynamicMenu,
  DynamicMenuKey,
  DynamicMenuSetting,
  LoadDynamicMenuDataPayload,
  SaveDynamicMenuDataPayload,
} from 'features/MenuBook/models/dynamicMenuSetting';
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import produce from 'immer';
import { ChangeEvent, CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, ModalProps, Row, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  COUPON_CLASSIFICATION,
  displayError,
  DYNAMIC_MENU_SIZE,
  DYNAMIC_MENU_SIZE1,
  DYNAMIC_MENU_SIZE2,
  DYNAMIC_MENU_SIZE3,
  DYNAMIC_MENU_SIZE4,
  DYNAMIC_MENU_SIZE5,
  handleServiceError,
  LANGUAGE_CODES,
  LANGUAGES,
} from 'utils';
import './style.scss';

interface DynamicMenuSettingModalProps {
  onClose?: (isChanged: boolean) => void;
  data: {
    subGenre: SubGenre;
    dataDate: Date;
  };
}

function DynamicMenuSettingModal({ data, onClose }: DynamicMenuSettingModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  //selector
  const isEditMode = useAppSelector(selectIsEditMode);
  const subGenre = data.subGenre;
  const dataDate = data.dataDate;

  // refs
  const languageElemRef = useRef<HTMLSelectElement>(null);
  const dynamicMenuFlgElemRef = useRef<HTMLInputElement>(null);
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const selectedMenuIdRef = useRef<number | null>(null);
  const menuSizeElemRefs = useRef<Array<HTMLSelectElement | null>>([]);

  // states
  const [selectedLanguage, setSelectedLanguage] = useState<string>(LANGUAGE_CODES.JA);
  const [dynamicMenuSettingData, setDynamicMenuSettingData] = useState<DynamicMenuSetting>({
    dynamic_menu_flg: false,
    menus: [],
  });
  const [showMenuModal, setShowMenuModal] = useState(false);

  // effect sides
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load data
  function loadData(isUpdate: boolean = false) {
    const payload: LoadDynamicMenuDataPayload = {
      genre_subgenre_id: subGenre.genre_subgenre_id as number,
      subgenre_menu_ids: subGenre.menus
        .filter((menu) => menu.subgenre_menu_id !== undefined)
        .map((filteredMenu) => filteredMenu.subgenre_menu_id as number),
    };
    (async () => {
      try {
        const response = await dynamicMenuService.loadDynamicMenuData(payload);
        const newDynamicMenuSettingData = response.data;
        if (isUpdate) {
          for (const menuSizeElemRef of menuSizeElemRefs.current) {
            if (menuSizeElemRef) {
              const menu = newDynamicMenuSettingData.menus.find(
                (item) => item.menu_id === Number(menuSizeElemRef.dataset.menuid)
              );
              if (menu) {
                menu.dynamic_menu_size = Number(menuSizeElemRef.value);
                menu.dynamic_menu_size = menu.dynamic_menu_size === 0 ? null : menu.dynamic_menu_size;

                if (menu.dynamic_menu_size === DYNAMIC_MENU_SIZE1) {
                  if (menu.img_1x2_ja === null || menu.img_1x2_ja.length === 0) {
                    menu.dynamic_menu_size = null;
                    menuSizeElemRef.value = '0';
                  }
                } else if (menu.dynamic_menu_size === DYNAMIC_MENU_SIZE2) {
                  if (menu.img_2x1_ja === null || menu.img_2x1_ja.length === 0) {
                    menu.dynamic_menu_size = null;
                    menuSizeElemRef.value = '0';
                  }
                } else if (menu.dynamic_menu_size === DYNAMIC_MENU_SIZE3) {
                  if (menu.img_2x2_ja === null || menu.img_2x2_ja.length === 0) {
                    menu.dynamic_menu_size = null;
                    menuSizeElemRef.value = '0';
                  }
                } else if (menu.dynamic_menu_size === DYNAMIC_MENU_SIZE4) {
                  if (menu.img_3x1_ja === null || menu.img_3x1_ja.length === 0) {
                    menu.dynamic_menu_size = null;
                    menuSizeElemRef.value = '0';
                  }
                } else if (menu.dynamic_menu_size === DYNAMIC_MENU_SIZE5) {
                  if (menu.img_3x2_ja === null || menu.img_3x2_ja.length === 0) {
                    menu.dynamic_menu_size = null;
                    menuSizeElemRef.value = '0';
                  }
                }
              }
            }
          }

          if (dynamicMenuFlgElemRef.current) {
            newDynamicMenuSettingData.dynamic_menu_flg = dynamicMenuFlgElemRef.current.checked;
          }
        }

        setDynamicMenuSettingData(newDynamicMenuSettingData);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  // events
  function handleClose(isChanged: boolean = false) {
    onClose?.(isChanged);
  }

  function handleEnableDynamicMenuChange() {
    if (dynamicMenuFlgElemRef.current) {
      setDynamicMenuSettingData({ ...dynamicMenuSettingData, dynamic_menu_flg: dynamicMenuFlgElemRef.current.checked });
    }
  }

  function handleLanguageChange() {
    if (languageElemRef.current) {
      loadData(true);
      setSelectedLanguage(languageElemRef.current.value);
    }
  }

  function handleMenuSizeChange(e: ChangeEvent<HTMLSelectElement>, menu: DynamicMenu) {
    const sizeValue = e.target.value && e.target.value !== '0' ? Number(e.target.value) : null;

    const nextState = produce(dynamicMenuSettingData, (draft) => {
      draft?.menus.forEach((item) => {
        if (item.menu_id === menu.menu_id) {
          item.dynamic_menu_size = sizeValue;
        }
      });
    });
    setDynamicMenuSettingData(nextState);
  }

  function renderImage(menu: DynamicMenu) {
    if (menu.dynamic_menu_size === null) {
      if (menu.img && menu.img.length > 0) {
        return <img style={{ maxWidth: 178 }} src={process.env.REACT_APP_IMAGE_URL + menu.img} alt="" />;
      }
    } else {
      const size = DYNAMIC_MENU_SIZE[menu.dynamic_menu_size];
      const keyImage: DynamicMenuKey = `img_${size}_${selectedLanguage}` as DynamicMenuKey;
      const keyImageDefault: DynamicMenuKey = `img_${size}_ja` as DynamicMenuKey;
      let imageSrc: string | null = null;

      let style: CSSProperties = { maxWidth: '100%' };
      if (size === DYNAMIC_MENU_SIZE['1']) {
        style = { maxWidth: 178 };
      } else if (size === DYNAMIC_MENU_SIZE['2'] || size === DYNAMIC_MENU_SIZE['3']) {
        style = { maxWidth: 368 };
      } else if (size === DYNAMIC_MENU_SIZE['4'] || size === DYNAMIC_MENU_SIZE['5']) {
        style = { maxWidth: 558 };
      }

      if (menu[keyImage] && (menu[keyImage] as string).length > 0) {
        imageSrc = menu[keyImage] as string | null;
      } else {
        imageSrc = menu[keyImageDefault] as string | null;
      }

      if (style && imageSrc && imageSrc.length > 0) {
        return <img style={style} src={process.env.REACT_APP_IMAGE_URL + imageSrc} alt="" />;
      }
    }

    return <div className="text-center">画像が設定されていません。</div>;
  }

  // menu modal events
  function handleOpenMenuModal(menuId: number) {
    setShowMenuModal(true);
    selectedMenuIdRef.current = menuId;
  }

  function handleOpenMenuDetailModal() {
    if (selectedMenuIdRef.current && menuDetailModalRef.current) {
      const menuId = selectedMenuIdRef.current;
      menuDetailModalRef.current.openModalExternal(menuId, dataDate);
    }
  }

  function handleMenuDetailModalClose() {
    if (menuDetailModalRef.current) {
      menuDetailModalRef.current.onReset();
      setShowMenuModal(false);
      selectedMenuIdRef.current = null;
    }
  }

  useEffect(() => {
    if (showMenuModal) {
      handleOpenMenuDetailModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuModal]);

  function handleMenuDetailModalSaveSuccess() {
    loadData(true);
  }

  function handleSave() {
    // compose payload
    const payload: SaveDynamicMenuDataPayload = {
      genre_subgenre_id: subGenre.genre_subgenre_id as number,
      dynamic_menu_flg: dynamicMenuFlgElemRef.current?.checked || false,
      menu_size: [],
    };

    if (menuSizeElemRefs.current.length > 0) {
      let isValid = true;
      let i = 0;
      for (const menuSizeElemRef of menuSizeElemRefs.current) {
        if (menuSizeElemRef) {
          let menuSizeValue: number | null = null;
          if (menuSizeElemRef.value !== '0') {
            menuSizeValue = Number(menuSizeElemRef.value);
          }

          if (menuSizeValue === null) {
            if (dynamicMenuSettingData.menus[i].img === null || dynamicMenuSettingData.menus[i].img?.length === 0) {
              isValid = false;
              break;
            }
          } else {
            const size = DYNAMIC_MENU_SIZE[menuSizeValue];
            const keyImageDefault: DynamicMenuKey = `img_${size}_ja` as DynamicMenuKey;
            if (
              dynamicMenuSettingData.menus[i][keyImageDefault] === null ||
              (dynamicMenuSettingData.menus[i][keyImageDefault] as string).length === 0
            ) {
              isValid = false;
              break;
            }
          }

          payload.menu_size.push({
            subgenre_menu_id: dynamicMenuSettingData.menus[i].subgenre_menu_id,
            dynamic_menu_size: menuSizeValue,
          });
        }

        i += 1;
      }

      if (!isValid) {
        showGlobalModal(() => (
          <AlertModal
            title={t('App.warning')}
            message="画像が未設定のメニューがあるため保存できません。日本語の画像を設定してください。"
          />
        ));
      } else {
        try {
          (async () => {
            const response = await dynamicMenuService.saveDynamicMenuData(payload);
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
            ));
          })();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      }
    }
  }

  return (
    <Fragment>
      <Modal
        size="xl"
        dialogClassName="dynamic-menu-setting-modal"
        show
        backdrop="static"
        onHide={handleClose}
        centered
        keyboard
      >
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title="プロモーション画像設定" />
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <div className="dynamic-menu-content">
            <div className="dynamic-menu-list-section">
              <Row className="mb-3">
                <Col sm={6}>
                  <div className="fw-bold mb-2">サブジャンル: {subGenre.name}</div>
                  <div className="d-flex align-items-center gap-4">
                    <strong>プロモーション画像設定</strong>
                    <Form.Check
                      ref={dynamicMenuFlgElemRef}
                      type="switch"
                      className="dynamic-menu-switch"
                      label=""
                      checked={dynamicMenuSettingData.dynamic_menu_flg}
                      onChange={handleEnableDynamicMenuChange}
                    />
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="text-end mb-3">※登録されていない日本語以外の言語の画像は日本語で表示されます。</div>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="d-inline-flex gap-3">
                        <Form.Label column className="fw-bold">
                          言語：
                        </Form.Label>
                        <Form.Select
                          ref={languageElemRef}
                          onChange={handleLanguageChange}
                          className="text-center"
                          style={{ width: '140px' }}
                        >
                          {Object.keys(LANGUAGES).map((languageKey) => (
                            <option key={languageKey} value={languageKey}>
                              {LANGUAGES[languageKey]}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                    <div>
                      <Button className="btn--dark-green" style={{ minWidth: '80px' }} onClick={handleSave}>
                        保存
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* menu list table */}
              <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '750px', height: '750px' }}>
                <Table hover>
                  <thead>
                    <tr>
                      <th style={{ width: '6%' }}>No.</th>
                      <th style={{ width: '24%' }}>メニュー</th>
                      <th style={{ width: '16%' }}>サイズ設定</th>
                      <th style={{ width: '46%' }}>プロモーション画像</th>
                      <th style={{ width: '8%' }}>変更</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dynamicMenuSettingData.menus.map((item, index) => (
                      <tr key={item.menu_id}>
                        <td className="text-center">{index + 1}</td>
                        <td className="text-center wrap">
                          <a
                            className="px-0 btn btn-link"
                            style={{ minWidth: 'inherit', maxWidth: '180px' }}
                            rel="noreferrer"
                            tabIndex={-1}
                            onClick={handleOpenMenuModal.bind(null, item.menu_id)}
                          >
                            {item.menu_name}({COUPON_CLASSIFICATION[item.coupon_classification.toString()]})
                          </a>
                        </td>
                        <td className="text-center">
                          <Form.Select
                            ref={(el: HTMLSelectElement | null) => (menuSizeElemRefs.current[index] = el)}
                            className="text-center"
                            data-menuid={item.menu_id}
                            defaultValue={item.dynamic_menu_size ? item.dynamic_menu_size.toString() : '0'}
                            onChange={(e) => handleMenuSizeChange(e, item)}
                          >
                            {Object.keys(DYNAMIC_MENU_SIZE).map((sizeKey) => (
                              <option key={sizeKey} value={sizeKey}>
                                {DYNAMIC_MENU_SIZE[sizeKey]}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>{renderImage(item)}</td>
                        <td className="text-center">
                          <Button onClick={handleOpenMenuModal.bind(null, item.menu_id)}>変更</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
            <div className="dynamic-menu-preview-section">
              <div className="preview-section">
                <div className="preview-section-header">{subGenre.name}</div>
                <div className="preview-section-body">
                  <div className="preview-grid-container">
                    {dynamicMenuSettingData.menus.map((item, index) => {
                      // compose size display name
                      const sizeDisplayName = item.dynamic_menu_size
                        ? DYNAMIC_MENU_SIZE[item.dynamic_menu_size?.toString()]
                        : '1x1';

                      // compose size
                      const size = { col: 1, row: 1 };
                      const sizeParts = sizeDisplayName.split('x');
                      size.col = Number(sizeParts[0]);
                      size.row = Number(sizeParts[1]);

                      return (
                        <div
                          key={`preview-grid-item${item.menu_id}`}
                          className="preview-grid-item"
                          style={{
                            gridColumn: `span ${size.col}`,
                            gridRow: `span ${size.row}`,
                          }}
                        >
                          <div className="preview-grid-item-image">
                            <div style={{ width: '100%' }}>{renderImage(item)}</div>
                          </div>
                          <div className="preview-grid-item-content">
                            <div>
                              <span>{index + 1}</span>
                              <br />
                              <span>
                                ({size.col}x{size.row})
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {showMenuModal &&
        createPortal(
          <MenuDetailModal
            ref={menuDetailModalRef}
            onMenuModalClose={handleMenuDetailModalClose}
            menuCallBack={handleMenuDetailModalSaveSuccess}
          />,
          document.body
        )}
    </Fragment>
  );
}

export default DynamicMenuSettingModal;
